import { Image } from "../../assets/images";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useEtlynCms } from "@etlyn/etlyn-cms";
import { subscribeNewsletter } from "@etlyn/etlyn-cms/newsletter";
import { recordNewsletterSubscription } from "../../analytics";
import { getCompanyId } from "../../companyContext";
import { getBlogSlug } from "../../utils/slugify";
import { Footer } from "../../components/Footer";
import { SEOHead } from "../../components/SEOHead";
import { readBlogBootstrap } from "./blogBootstrap";

/* ── helpers ──────────────────────────────────────────────── */

function stripHtml(html: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function readingTime(html: string): number {
  const words = stripHtml(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 225));
}

function formatDate(raw?: string | number): string {
  if (!raw) return "";
  const d = new Date(typeof raw === "number" ? raw * 1000 : raw);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/* ── component ────────────────────────────────────────────── */

function BlogList() {
  const [searchParams] = useSearchParams();
  const { state: blogsState } = useEtlynCms("blogs-core");
  const [bootstrap] = useState(() => readBlogBootstrap('archive'));
  const submitting = useRef(false);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [device, setDevice] = useState("web");
  useEffect(() => {
    if (window.screen.width < 768) setDevice("mob");
  }, []);

  const isMob = device === "mob";

  const published = useMemo(
    () => (blogsState.companyId === getCompanyId() && !blogsState.loading && !blogsState.error
      ? blogsState.blogs : bootstrap || blogsState.blogs).filter((b: any) => b.published),
    [blogsState.blogs, blogsState.companyId, blogsState.loading, blogsState.error, bootstrap]
  );

  const featured = published.find((b: any) => b.featured) || published[0];
  const rest = published.filter((b: any) => b !== featured);


  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;

    const email = newsletterEmail.trim();
    if (!email) {
      setNewsletterStatus({ type: "error", message: "Please enter your email address." });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus({ type: "error", message: "Please enter a valid email." });
      return;
    }

    submitting.current = true;
    const controller = new AbortController();
    pending.current = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    setIsSubmittingNewsletter(true);
    setNewsletterStatus(null);

    try {
      await subscribeNewsletter({ email, source: "etlyn-com-blog" }, {
        apiBaseUrl: import.meta.env.VITE_API_URL,
        companyId: getCompanyId() || "",
        signal: controller.signal,
      });
      recordNewsletterSubscription();
      setNewsletterEmail("");
      setNewsletterStatus({
        type: "success",
        message: "You’re subscribed. Thank you.",
      });
    } catch {
      setNewsletterStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      clearTimeout(timeout);
      pending.current = null;
      submitting.current = false;
      setIsSubmittingNewsletter(false);
    }
  };

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 overflow-x-hidden mt-[80px] font-['Inter',sans-serif]">
      <SEOHead
        title="Blog — Insights & Articles on Software Development"
        description="Practical advice, strategies and case studies on building digital products that drive real business results. Tips on web development, CMS, e-commerce and growth."
        path="/blogs"
        robots={["q", "category", "page"].some(key => Boolean(searchParams.get(key))) ? "noindex,follow" : "index,follow"}
        keywords="software development blog, web development tips, CMS insights, business technology articles, digital product strategy"
      />

      {/* ─── Hero header ─────────────────────────────────── */}
      <section className="pt-8 pb-10 sm:pt-12 sm:pb-14 text-center">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
          Insights &amp; Ideas
        </span>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#333D51] tracking-tight mb-3 max-w-2xl mx-auto leading-tight">
          Practical advice to grow your business&nbsp;with&nbsp;technology
        </h1>
        <p className="text-sm sm:text-base text-[#666D7C] max-w-xl mx-auto leading-relaxed">
          Strategies, case studies and lessons we've learned building digital
          products that drive real results.
        </p>
      </section>

      {/* ─── Featured post ───────────────────────────────── */}
      {featured && (
        <section className="mb-14">
          <Link
            to={`/blogs/${getBlogSlug(featured)}`}
            className={`group relative overflow-hidden rounded-2xl bg-white ring-1 ring-[#DFDFE0]/60 transition-all hover:shadow-lg cursor-pointer ${
              isMob ? "flex flex-col" : "grid grid-cols-2"
            }`}
          >
            {/* Image */}
            {featured.cover_image_url && (
              <div className="relative aspect-[16/9] sm:aspect-auto overflow-hidden">
                <Image
                  src={featured.cover_image_url}
                  priority sizes="(max-width: 768px) 100vw, 600px"
                  alt={featured.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
              </div>
            )}

            {/* Content */}
            <div className="flex flex-col justify-center p-6 sm:p-10">
              <div className="flex items-center gap-3 mb-4">
                {featured.category && (
                  <span className="inline-block rounded-full bg-[#EEF1FB] px-3 py-1 text-[11px] font-semibold text-[#0D2B7E] uppercase tracking-wide">
                    {featured.category}
                  </span>
                )}
                <span className="text-xs text-[#808692]">
                  {formatDate(featured.published_at || featured.created_at)}
                </span>
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] leading-snug tracking-tight mb-3 group-hover:text-[#0D2B7E] transition-colors">
                {featured.title}
              </h2>

              <p className="text-sm text-[#666D7C] leading-relaxed line-clamp-3 mb-5">
                {featured.excerpt || stripHtml(featured.content).slice(0, 200)}
              </p>

              <div className="flex items-center gap-4 text-xs text-[#808692]">
                {featured.author && (
                  <span className="flex items-center gap-1.5">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0D2B7E] text-[10px] font-bold text-white">
                      {featured.author.charAt(0).toUpperCase()}
                    </span>
                    {featured.author}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  {readingTime(featured.content)} min read
                </span>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* ─── Post grid ───────────────────────────────────── */}
      {rest.length > 0 && (
        <section className="mb-20">
          <h2 className="text-lg font-semibold text-[#333D51] tracking-tight mb-6">
            All Articles
          </h2>

          <div className={`grid gap-6 ${isMob ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
            {rest.map((blog: any) => (
              <Link
                key={blog.id || blog.title}
                to={`/blogs/${getBlogSlug(blog)}`}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-[#DFDFE0]/60 transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
              >
                {/* Cover */}
                {blog.cover_image_url && (
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <Image
                      src={blog.cover_image_url}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                      alt={blog.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                )}

                {/* Body */}
                <div className="flex flex-1 flex-col p-5">
                  {/* Meta row */}
                  <div className="flex items-center gap-2 mb-3">
                    {blog.category && (
                      <span className="inline-block rounded-full bg-[#EEF1FB] px-2.5 py-0.5 text-[10px] font-semibold text-[#0D2B7E] uppercase tracking-wide">
                        {blog.category}
                      </span>
                    )}
                    <span className="text-[11px] text-[#808692]">
                      {formatDate(blog.published_at || blog.created_at)}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-[15px] font-semibold leading-snug text-[#333D51] mb-2 line-clamp-2 group-hover:text-[#0D2B7E] transition-colors">
                    {blog.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-[13px] text-[#666D7C] leading-relaxed line-clamp-2 mb-4">
                    {blog.excerpt || stripHtml(blog.content).slice(0, 120)}
                  </p>

                  {/* Footer */}
                  <div className="mt-auto flex items-center justify-between pt-3 border-t border-[#DFDFE0]/60">
                    <div className="flex items-center gap-2">
                      {blog.author && (
                        <>
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0D2B7E] text-[9px] font-bold text-white">
                            {blog.author.charAt(0).toUpperCase()}
                          </span>
                          <span className="text-[11px] text-[#666D7C]">
                            {blog.author}
                          </span>
                        </>
                      )}
                    </div>
                    <span className="flex items-center gap-1 text-[11px] text-[#808692]">
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                      {readingTime(blog.content)} min
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ─── Empty state ─────────────────────────────────── */}
      {published.length === 0 && (
        <section className="py-20 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EEF1FB]">
            <svg className="h-7 w-7 text-[#0D2B7E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 0 1-2.25 2.25H5.625a2.25 2.25 0 0 1-2.25-2.25V8.625c0-.621.504-1.125 1.125-1.125H6.75" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-[#333D51] mb-1">
            No articles yet
          </h3>
          <p className="text-sm text-[#808692]">
            Check back soon — we're working on something great.
          </p>
        </section>
      )}

      {/* ─── Newsletter CTA ──────────────────────────────── */}
      <section className="mb-16 rounded-2xl bg-gradient-to-br from-[#0D2B7E] to-[#25408B] px-6 py-12 sm:px-12 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 tracking-tight">
          Stay ahead of the curve
        </h2>
        <p className="text-sm text-white/70 mb-6 max-w-md mx-auto leading-relaxed">
          Get our latest insights on technology, growth and digital strategy
          delivered straight to your inbox.
        </p>
        <form onSubmit={handleNewsletterSubmit} className="mx-auto flex w-full max-w-md flex-col gap-3 sm:flex-row sm:items-stretch">
          <input
            type="email"
            aria-label="Email address"
            required
            value={newsletterEmail}
            onChange={(e) => {
              setNewsletterEmail(e.target.value);
              if (newsletterStatus) setNewsletterStatus(null);
            }}
            placeholder="your@email.com"
            disabled={isSubmittingNewsletter}
            className="h-11 w-full rounded-lg border border-white/30 bg-white/95 px-4 text-sm text-[#333D51] placeholder:text-[#808692]/70 outline-none transition focus:border-white focus:ring-2 focus:ring-white/25 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={isSubmittingNewsletter}
            className="inline-flex h-11 shrink-0 items-center justify-center rounded-lg bg-white px-5 text-sm font-semibold text-[#0D2B7E] shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {isSubmittingNewsletter ? "Submitting..." : "Subscribe"}
          </button>
        </form>
        {newsletterStatus && (
          <p
            role={newsletterStatus.type === "error" ? "alert" : "status"}
            className={`mt-3 text-xs ${
              newsletterStatus.type === "success" ? "text-[#C8FACC]" : "text-[#FFD7DE]"
            }`}
          >
            {newsletterStatus.message}
          </p>
        )}
      </section>

      <Footer isMob={isMob} />
    </div>
  );
}

export default BlogList;
